import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { type LucideIcon } from "lucide-react";

const colorCycle = [
  "bg-chathero-mint/20 text-chathero-mint",
  "bg-chathero-orange/20 text-chathero-orange",
  "bg-primary/20 text-primary",
];

interface Props {
  translationPrefix: string;
  icons: LucideIcon[];
}

const SolutionFeaturesI18n = ({ translationPrefix, icons }: Props) => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject } = useLanguage();
  const items = tObject<Array<{ title: string; description: string; highlight: string }>>(`${translationPrefix}.features.items`);

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn("py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center"><Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t(`${translationPrefix}.features.badge`)}</Badge></div>
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6 text-center">{t(`${translationPrefix}.features.title`)}</h2>
          <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">{t(`${translationPrefix}.features.intro`)}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((feature, index) => {
              const Icon = icons[index] || icons[0];
              const color = colorCycle[index % 3];
              return (
                <div key={index} className={cn("bg-background rounded-2xl p-6 border border-border transition-all duration-500 hover:shadow-lg group", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")} style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms" }}>
                  <div className={cn("w-14 h-14 rounded-xl flex items-center justify-center mb-4", color.split(" ")[0])}>
                    <Icon className={cn("w-7 h-7", color.split(" ")[1])} />
                  </div>
                  <span className={cn("inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3", color)}>{feature.highlight}</span>
                  <h3 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionFeaturesI18n;
